// packages/studio/web/src/proposals/ProposalList.tsx
//
// /studio/:slug/:repo/_/proposals — the changesets for a repo, filterable by state.

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "../ui/PageHeader";
import { SkeletonRows } from "../ui/Skeleton";
import { Banner } from "../ui/Banner";
import { formatRelativeDate } from "../details/relativeDate";
import { cx } from "../lib/classNames";
import { StateBadge } from "./StateBadge";
import { useMe } from "../api/hooks/useMe";
import { listChangesets, type Changeset, type ChangesetState } from "./proposalsApi";
import { changeset as changesetPath, repoHome } from "../lib/paths";
import { useTranslation } from "../i18n";
import { useContextMenu, type ContextMenuItem } from "../ui/ContextMenu";

const TABS: { state: ChangesetState; labelKey: string }[] = [
  { state: "proposed", labelKey: "list.tabOpen" },
  { state: "draft", labelKey: "list.tabDrafts" },
  { state: "accepted", labelKey: "list.tabAccepted" },
  { state: "archived", labelKey: "list.tabArchived" },
];

const EMPTY_KEY: Record<ChangesetState, string> = {
  proposed: "list.emptyProposed",
  draft: "list.emptyDraft",
  accepted: "list.emptyAccepted",
  archived: "list.emptyArchived",
};

export function ProposalList() {
  const { t } = useTranslation("proposals");
  const { slug = "", repo = "" } = useParams();
  const navigate = useNavigate();
  const menu = useContextMenu();
  const [tab, setTab] = useState<ChangesetState>("proposed");
  const meId = useMe().data?.id ?? null;
  const isMine = (cs: Changeset) => meId != null && cs.authorUserId === meId;

  function itemsFor(cs: Changeset): ContextMenuItem[] {
    const path = `${changesetPath(slug, repo, cs.number)}?view=diff`;
    return [
      { label: t("menu.open"), onClick: () => navigate(path) },
      {
        label: t("menu.copyLink"),
        onClick: () => void navigator.clipboard?.writeText(`${location.origin}${path}`),
      },
      {
        label: t("menu.copyNumber"),
        onClick: () => void navigator.clipboard?.writeText(`#${cs.number}`),
      },
    ];
  }

  const query = useQuery({
    queryKey: ["changesets", slug, repo, tab],
    queryFn: () => listChangesets(slug, repo, { state: tab }),
  });

  return (
    <div className="mx-auto w-full max-w-3xl p-6">
      <PageHeader
        title={t("list.title")}
        description={<span className="font-mono text-xs text-slate-500">{repo}</span>}
        actions={
          <Link
            to={repoHome(slug, repo)}
            className="inline-flex items-center gap-1.5 rounded-md bg-arch-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-arch-500"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            {t("list.newChange")}
          </Link>
        }
      />

      <div className="mb-5 flex gap-1 border-b border-slate-800">
        {TABS.map((tabDef) => (
          <button
            key={tabDef.state}
            type="button"
            onClick={() => setTab(tabDef.state)}
            aria-pressed={tab === tabDef.state}
            className={cx(
              "-mb-px border-b-2 px-3 py-2 text-sm transition-colors",
              tab === tabDef.state
                ? "border-arch-500 font-medium text-slate-100"
                : "border-transparent text-slate-400 hover:text-slate-200",
            )}
          >
            {t(tabDef.labelKey)}
          </button>
        ))}
      </div>

      {query.isLoading ? (
        <SkeletonRows rows={4} />
      ) : query.error ? (
        <Banner kind="error">{String(query.error)}</Banner>
      ) : query.data && query.data.length === 0 ? (
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-12 text-center">
          <p className="text-sm text-slate-500">{t(EMPTY_KEY[tab])}</p>
        </div>
      ) : (
        <ul className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900 divide-y divide-slate-800">
          {query.data?.map((cs) => (
            <li
              key={cs.id}
              className="group flex items-center gap-3 px-4 py-3 hover:bg-slate-800/40"
              onContextMenu={(e) => menu.open(e, itemsFor(cs))}
            >
              <StateBadge state={cs.state} mine={isMine(cs)} />
              <div className="min-w-0 flex-1">
                {/* Open to review in the editor's central diff (read-only for
                    changes you didn't author). */}
                <Link
                  to={`${changesetPath(slug, repo, cs.number)}?view=diff`}
                  className="block truncate text-sm font-medium text-slate-100 hover:text-arch-300"
                >
                  {cs.title || t("list.untitled")}
                </Link>
                <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                  {cs.featureBranch ? (
                    <span className="truncate font-mono">{cs.featureBranch}</span>
                  ) : (
                    <span>{t("list.onBranch", { branch: cs.trunkBranch })}</span>
                  )}
                  <span aria-hidden="true">·</span>
                  <span>{t("list.updated", { when: formatRelativeDate(cs.updatedAt) })}</span>
                </div>
              </div>
              {/* Only the author can edit. */}
              {isMine(cs) && (cs.state === "draft" || cs.state === "proposed") && (
                <Link
                  to={changesetPath(slug, repo, cs.number)}
                  className="rounded-md border border-slate-700 px-2.5 py-1 text-xs text-slate-300 opacity-0 transition-opacity hover:bg-slate-800 group-hover:opacity-100 focus:opacity-100"
                >
                  {t("list.edit")}
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}
      {menu.node}
    </div>
  );
}
